import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { ApiCallError, callFunction } from '@/lib/api';
import { latestXdis16ExchangeId, xdis16VersionText } from '@/lib/xdis/xdisView';
import { XdisViewer } from './XdisViewer';
import { Xdis18Form } from './Xdis18Form';
import { Xdis21Form } from './Xdis21Form';
import { Xdis21PlanList } from './Xdis21PlanList';
import { HospitalReferralOutbox } from '@/components/patient/HospitalReferralOutbox';
import { formatDanishDateTime } from '@/lib/danishDateTime';

// Spor A block 3, T3.1 (XDIS-EPJ 3.1–3.7): received XDIS16 indlæggelsesrapporter. A report
// with the same EpisodeOfCareIdentifier as an earlier one is an updated version and is
// marked so; ContentManuallyUpdated is shown. Each report opens with MedCom's stylesheet.
// With a patient (patient tab), "Udskrivningsrapport (XDIS18)" starts T3.3 from it.

export type Xdis16Row = {
  exchangeId: string;
  receivedAt: string;
  episodeOfCareIdentifier: string | null;
  senderName: string | null;
  senderEan: string | null;
  patientName: string | null;
  patientId: string | null;
  patientIdKind: 'cpr' | 'alternative' | 'none';
  sentAt: string | null;
  validationOk: boolean;
  contentManuallyUpdated: boolean;
  version: number;
  versions: number;
  updated: boolean;
};

type Viewed = { exchangeId: string; kind: string; patientId: string | null; xml: string };

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : formatDanishDateTime(d);
}

export function ReceivedXdis16Panel({
  clinicId,
  patientId,
  patientName,
  canRevealCpr = true,
  refreshKey = 0,
}: {
  clinicId: string;
  refreshKey?: number;
  patientId?: string;
  patientName?: string | null;
  canRevealCpr?: boolean;
}) {
  const { t } = useTranslation();
  const [viewed, setViewed] = useState<Viewed | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  const [formFor, setFormFor] = useState<string | null>(null);
  const [form21For, setForm21For] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['communicate-xdis16', clinicId, patientId ?? null, refreshKey],
    staleTime: 30_000,
    queryFn: () =>
      callFunction<Record<string, unknown>, { rows: Xdis16Row[] }>('communicate-xdis', {
        action: 'list16',
        clinic_id: clinicId,
        ...(patientId ? { patient_id: patientId } : {}),
      }),
  });

  async function onView(row: Xdis16Row) {
    setViewing(row.exchangeId);
    setViewError(null);
    try {
      setViewed(await callFunction<Record<string, unknown>, Viewed>('communicate-xdis', { action: 'view', clinic_id: clinicId, exchange_id: row.exchangeId }));
    } catch (err) {
      setViewError(err instanceof ApiCallError ? err.message : t('patients.xdis.view_failed', 'Rapporten kunne ikke vises.'));
    } finally {
      setViewing(null);
    }
  }

  const rows = list.data?.rows ?? [];
  const panelHeading = (
    <div className="flex items-center justify-between gap-2">
      <h3 className="text-sm font-semibold">{t('patients.xdis.received16_title', 'Modtagne indlæggelsesrapporter (XDIS16)')}</h3>
      <Button type="button" size="sm" variant="secondary" onClick={() => void list.refetch()} disabled={list.isFetching}>
        {list.isFetching ? t('medcom.correspondence.refreshing', 'Opdaterer…') : t('medcom.correspondence.refresh', 'Opdatér')}
      </Button>
    </div>
  );
  if (form21For && patientId) {
    return (
      <section className="space-y-3 rounded-md border border-ds-line p-4" data-testid="received-xdis16">
        {panelHeading}
        <Xdis21Form
          clinicId={clinicId}
          patientId={patientId}
          patientName={patientName ?? rows.find((row) => row.exchangeId === form21For)?.patientName ?? null}
          sourceExchangeId={form21For}
          canRevealCpr={canRevealCpr}
          onClose={() => setForm21For(null)}
        />
      </section>
    );
  }
  if (formFor && patientId) {
    return (
      <section className="space-y-3 rounded-md border border-ds-line p-4" data-testid="received-xdis16">
        {panelHeading}
        <Xdis18Form
          clinicId={clinicId}
          patientId={patientId}
          patientName={patientName ?? rows.find((row) => row.exchangeId === formFor)?.patientName ?? null}
          sourceExchangeId={formFor}
          canRevealCpr={canRevealCpr}
          onClose={() => setFormFor(null)}
        />
      </section>
    );
  }
  return (
    <section className="space-y-3 rounded-md border border-ds-line p-4" data-testid="received-xdis16">
      {panelHeading}
      {list.error && (
        <p role="alert" className="text-sm text-ds-red-ink">
          {list.error instanceof ApiCallError ? list.error.message : t('patients.xdis.list_failed', 'Indlæggelsesrapporter kunne ikke hentes.')}
        </p>
      )}
      {list.isSuccess && rows.length === 0 && <p className="text-sm text-ds-muted">{t('patients.xdis.received16_empty', 'Ingen modtagne indlæggelsesrapporter.')}</p>}
      <ul className="space-y-2">
        {rows.map((row) => (
          <li key={row.exchangeId} className="space-y-1 rounded-md bg-ds-sunk p-3 text-sm" data-testid="xdis16-row">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-medium">
                {row.senderName ?? row.senderEan ?? '–'}
                {!patientId && row.patientName ? ` · ${row.patientName}` : ''}
              </span>
              <span className="flex items-center gap-2 text-xs">
                <span
                  className={row.updated ? 'rounded bg-ds-amber-tint px-1.5 py-0.5 font-semibold text-ds-amber-ink  ' : 'text-ds-muted'}
                  data-testid="xdis16-version"
                >
                  {xdis16VersionText(row)}
                </span>
                {!row.validationOk && <span className="text-ds-red-ink">{t('patients.xdis.invalid', 'Fejl i validering')}</span>}
              </span>
            </div>
            <p className="text-xs text-ds-muted">
              {t('patients.xdis.episode', 'Forløb')}: <span className="font-mono">{row.episodeOfCareIdentifier ?? '–'}</span>
              {row.sentAt ? ` · ${t('patients.xdis.sent', 'Sendt')} ${row.sentAt}` : ''}
              {` · ${t('medcom.correspondence.received', 'Modtaget')} ${formatTime(row.receivedAt)}`}
            </p>
            {row.contentManuallyUpdated && (
              <p className="text-xs font-medium text-ds-amber-ink" data-testid="xdis16-manually-updated">
                {t('patients.xdis.manually_updated', 'Indholdet er opdateret manuelt af afsender.')}
              </p>
            )}
            {!patientId && !row.patientId && (
              <p className="text-xs text-ds-amber-ink">{t('patients.xdis.no_patient', 'Ingen patient i klinikken med dette CPR-nummer.')}</p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="secondary" disabled={viewing === row.exchangeId} onClick={() => void onView(row)}>
                {t('patients.xdis.view', 'Vis')}
              </Button>
              {patientId && row.patientId === patientId && (
                <>
                  <Button type="button" size="sm" onClick={() => setForm21For(row.exchangeId)} data-testid="xdis16-make-xdis21">
                    {t('patients.xdis.form21_title', 'Plejeforløbsplan (XDIS21)')}
                  </Button>
                  <Button type="button" size="sm" onClick={() => setFormFor(row.exchangeId)} data-testid="xdis16-make-xdis18">
                    {t('patients.xdis.form18_title', 'Udskrivningsrapport (XDIS18)')}
                  </Button>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
      {viewError && <p role="alert" className="text-sm text-ds-red-ink">{viewError}</p>}
      {viewed && (
        <div className="space-y-2">
          <div className="flex justify-end">
            <Button type="button" size="sm" variant="secondary" onClick={() => setViewed(null)}>
              {t('patients.xdis.close', 'Luk')}
            </Button>
          </div>
          <XdisViewer
            xml={viewed.xml}
            kind={viewed.kind}
            patientId={viewed.patientId}
            patientName={patientName ?? rows.find((r) => r.exchangeId === viewed.exchangeId)?.patientName ?? null}
            canRevealCpr={canRevealCpr}
          />
        </div>
      )}
      {patientId && (
        <>
          <Xdis21PlanList
            clinicId={clinicId}
            patientId={patientId}
            patientName={patientName ?? null}
            sourceExchangeId={latestXdis16ExchangeId(
              rows
                .filter((r) => r.patientId === patientId)
                .map((r) => ({ exchangeId: r.exchangeId, at: r.sentAt ?? r.receivedAt })),
            )}
            canRevealCpr={canRevealCpr}
          />
          <HospitalReferralOutbox
            clinicId={clinicId}
            patientId={patientId}
            revision={0}
            // XDIS-EPJ: XDIS21/XDIS18/XDIS91 share the outbox row (envelope, 'Hent XML',
            // 'Pak i VANSEnvelope').
            kinds={['XDIS21', 'XDIS18', 'XDIS91']}
            title={t('patients.xdis.outbox_title', 'Udbakke – plejeforløbsplaner (XDIS21), udskrivningsrapporter (XDIS18) og korrespondancer (XDIS91)')}
            emptyText={t('patients.xdis.outbox_empty', 'Ingen plejeforløbsplaner, udskrivningsrapporter eller korrespondancer i udbakken.')}
          />
        </>
      )}
    </section>
  );
}
